/**
 * <variant-picker> custom element, shared by quick view and the product page.
 *
 * Markup contract:
 *   <div data-variant-scope>                      scope for everything below (falls back to the closest form)
 *     <variant-picker data-section="{{ section.id }}" update-url>
 *       <script type="application/json" data-variants>{{ product.variants | json }}</script>
 *       <fieldset data-option-index="0">
 *         <legend>Color <span data-selected-value>Black</span></legend>
 *         <input type="radio" name="..." value="Black" checked> <label>...</label>
 *       </fieldset>
 *     </variant-picker>
 *     <div class="price" data-price>...</div>     (snippets/price.liquid)
 *     <input type="hidden" name="id" value="...">
 *     <button data-add-button><span data-add-label>Add to cart</span></button>
 *   </div>
 *
 * On change it finds the matching variant, updates name="id", re-renders every [data-price] in
 * scope with Lumi.formatMoney, updates add buttons ("Add to cart" / "Sold out" / "Unavailable"),
 * marks option values with no available combination (data-unavailable on the input, class
 * is-unavailable on its label), replaces ?variant= when update-url is present, and emits
 * lumi:variant:changed { variant, sectionId, picker } on document.
 */
(function () {
  if (customElements.get('variant-picker')) return;

  const Lumi = (window.Lumi = window.Lumi || {});
  const str = (key, fallback) => (Lumi.strings && Lumi.strings[key]) || fallback;
  const money = (cents) => (Lumi.formatMoney ? Lumi.formatMoney(cents) : (cents / 100).toFixed(2));

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      const data = this.querySelector('script[data-variants]');
      try {
        this.variants = data ? JSON.parse(data.textContent) : [];
      } catch (e) {
        this.variants = [];
      }
      this.addEventListener('change', (e) => {
        if (e.target.matches('input[type="radio"], select')) this.update();
      });
      this.markAvailability();
    }

    get scope() {
      return this.closest('[data-variant-scope]') || this.closest('form') || this.parentElement || document;
    }

    get fieldsets() {
      return [...this.querySelectorAll('[data-option-index]')];
    }

    selectedValues() {
      return this.fieldsets.map((fs) => {
        const checked = fs.querySelector('input[type="radio"]:checked');
        if (checked) return checked.value;
        const select = fs.querySelector('select');
        return select ? select.value : null;
      });
    }

    currentVariant() {
      const values = this.selectedValues();
      return this.variants.find((v) => values.every((val, i) => v.options[i] === val)) || null;
    }

    update() {
      const variant = this.currentVariant();
      const values = this.selectedValues();

      this.fieldsets.forEach((fs, i) => {
        const label = fs.querySelector('[data-selected-value]');
        if (label) label.textContent = values[i] || '';
      });

      this.markAvailability();
      this.render(variant);

      if (variant && this.hasAttribute('update-url')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState(window.history.state, '', url.toString());
      }

      if (Lumi.emit) Lumi.emit('lumi:variant:changed', { variant, sectionId: this.dataset.section || null, picker: this });
    }

    // A value is unavailable when no available variant has it together with the other current selections.
    markAvailability() {
      const values = this.selectedValues();
      this.fieldsets.forEach((fs, index) => {
        fs.querySelectorAll('input[type="radio"]').forEach((input) => {
          const ok = this.variants.some(
            (v) => v.available && v.options[index] === input.value && values.every((val, j) => j === index || val === null || v.options[j] === val)
          );
          input.toggleAttribute('data-unavailable', !ok);
          const label = input.closest('label') || (input.id && fs.querySelector(`label[for="${CSS.escape(input.id)}"]`));
          if (label) {
            label.classList.toggle('is-unavailable', !ok);
            // Screen readers hear the sold out state, not just see the strike.
            let note = label.querySelector('[data-unavailable-note]');
            if (!ok && !note) {
              note = document.createElement('span');
              note.className = 'visually-hidden';
              note.setAttribute('data-unavailable-note', '');
              note.textContent = `, ${str('soldOut', 'Sold out')}`;
              label.appendChild(note);
            } else if (ok && note) {
              note.remove();
            }
          }
        });
      });
    }

    render(variant) {
      const scope = this.scope;

      scope.querySelectorAll('input[name="id"]').forEach((input) => {
        if (variant) input.value = variant.id;
        input.disabled = !variant;
      });

      scope.querySelectorAll('[data-price]').forEach((el) => {
        if (!variant) return;
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        el.classList.toggle('price--sale', !!onSale);
        el.innerHTML = onSale
          ? `<span class="visually-hidden">${str('salePrice', 'Sale price')}</span><span class="price__current">${money(variant.price)}</span>` +
            `<span class="visually-hidden">${str('regularPrice', 'Regular price')}</span><s class="price__compare">${money(variant.compare_at_price)}</s>`
          : `<span class="price__current">${money(variant.price)}</span>`;
      });

      scope.querySelectorAll('[data-add-button]').forEach((button) => {
        const available = !!(variant && variant.available);
        button.disabled = !available;
        const label = button.querySelector('[data-add-label]') || button;
        label.textContent = !variant ? str('unavailable', 'Unavailable') : available ? str('addToCart', 'Add to cart') : str('soldOut', 'Sold out');
      });
    }
  }

  customElements.define('variant-picker', VariantPicker);
})();
